import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import type {
  AwsCredentialIdentity,
  AwsCredentialIdentityProvider,
  InitializeHandlerArguments,
  InitializeMiddleware,
  MiddlewareStack,
} from '@smithy/types';
import { getConfigValue } from '@/services/configService';
import { authService } from '@/services/authService';

export interface AWSClientConfig {
  region: string;
  credentials: AwsCredentialIdentityProvider;
}

const DEFAULT_REGION = 'us-east-1';

/** Refresh cached credentials this long before they actually expire. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/** A sign-in token this close to expiry is refreshed before the identity pool sees it. */
const TOKEN_MIN_VALIDITY_MS = 60 * 1000;

/** Retry posture for the credential-chain clients (Cognito Identity + STS). */
const CHAIN_RETRY = { retryMode: 'adaptive', maxAttempts: 5 } as const;

const NOT_SIGNED_IN = 'User is not authenticated. Please sign in again.';
const NO_CONFIG = 'Portal configuration not available. Please reload the page.';

/**
 * True for the refusal a revoke gives (TASK-629 item 6). When portal-admin
 * deletes a portal person, it denies every session of the browser role issued
 * before that moment. AWS then answers a call made with older credentials
 * "with an explicit deny in an identity-based policy". Nothing else on the
 * browser role denies explicitly: a right the role lacks says "no
 * identity-based policy allows", and that is never retried.
 */
export function isRevokedCredentialsError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: unknown; Code?: unknown; message?: unknown; $metadata?: { httpStatusCode?: number } };
  const denied =
    /AccessDenied/i.test(String(err.name ?? '')) ||
    /AccessDenied/i.test(String(err.Code ?? '')) ||
    err.$metadata?.httpStatusCode === 403;
  return denied && /explicit deny in an identity-based policy/i.test(String(err.message ?? ''));
}

/** The session, with its ID token refreshed when it is about to expire. */
async function currentSession() {
  const session = (await authService.ensureValidSession(TOKEN_MIN_VALIDITY_MS)) || authService.getCurrentSession();
  if (!session) throw new Error(NOT_SIGNED_IN);
  return session;
}

/** The credential provider an SDK client keeps. It memoises, and `forceRefresh` asks the page's provider again. */
type ClientCredentials = (options?: { forceRefresh?: boolean }) => Promise<AwsCredentialIdentity>;

/** The part of an AWS SDK v3 client the revoke retry needs. Any client's input and output types fit. */
interface SdkClient {
  config: object;
  middlewareStack: MiddlewareStack<any, any>;
}

/**
 * The page's AWS credentials, for the deployer account only. Every AWS client
 * in the page takes them from here, so the page holds one set of credentials
 * for each person and asks the identity pool again only near their expiry, or
 * after a revoke.
 *
 * The browser never holds credentials for a client account: those calls go
 * through the client access Lambda (`clientAccess`, TASK-625).
 */
class AWSCredentialsService {
  /** Which person the cached credentials belong to. A sign-in as somebody else must not reuse them. */
  private owner: string | null = null;
  private cached: AwsCredentialIdentity | null = null;
  private fetchedAt = 0;
  private pending: Promise<AwsCredentialIdentity> | null = null;

  /** True in a browser once the portal config names the identity pool and the user pool. */
  isConfigured(): boolean {
    return typeof window !== 'undefined' && !!getConfigValue('IDENTITY_POOL_ID') && !!getConfigValue('USER_POOL_ID');
  }

  /**
   * The provider to give an AWS client. It checks the sign-in each time the
   * client asks, so a client kept across a sign-out gets no credentials.
   */
  credentials(): AwsCredentialIdentityProvider {
    return this.provider;
  }

  /** The provider, or undefined outside a browser or before the portal config has loaded. */
  credentialsIfConfigured(): AwsCredentialIdentityProvider | undefined {
    return this.isConfigured() ? this.provider : undefined;
  }

  /** The provider, after a check that the portal config has loaded and a person is signed in. */
  async getDeployerCredentials(): Promise<AwsCredentialIdentityProvider> {
    if (!this.isConfigured()) throw new Error(NO_CONFIG);
    if (!authService.getCurrentSession()) throw new Error(NOT_SIGNED_IN);
    return this.provider;
  }

  /** The region and the provider for a deployer-account client. */
  async getDeployerClientConfig(region?: string): Promise<AWSClientConfig> {
    const credentials = await this.getDeployerCredentials();
    return {
      region: region || getConfigValue('AWS_REGION') || DEFAULT_REGION,
      credentials,
    };
  }

  /**
   * Makes a client send a call once more, with new credentials, when AWS
   * refuses it because a delete revoked the credentials it held. A person in
   * an open tab then sees no error. Every AWS client the page makes goes
   * through this.
   *
   * The retry runs before the client signs the call, so the second send is
   * signed again. A refused call never ran, so a second send is safe.
   */
  withRevokedRetry<C extends SdkClient>(client: C): C {
    const retry: InitializeMiddleware<any, any> = (next) => async (args: InitializeHandlerArguments<any>) => {
      try {
        return await next(args);
      } catch (error) {
        if (!isRevokedCredentialsError(error)) throw error;
        const credentials = (client.config as { credentials?: ClientCredentials }).credentials;
        if (!credentials) throw error;
        // The client keeps its own copy of the credentials. Drop the page's
        // copy only if it is the one refused, then make the client ask again.
        this.discard((await credentials()).accessKeyId);
        await credentials({ forceRefresh: true });
        return await next(args);
      }
    };
    client.middlewareStack.add(retry, { step: 'initialize', name: 'portalRevokedCredentialsRetry', override: true });
    return client;
  }

  /**
   * The same retry for a call made through a library that builds its own
   * client for each call (`@arcanumai/client-config`). The second call builds
   * a new client, which asks the page for credentials again.
   */
  async retryIfRevoked<T>(call: () => Promise<T>): Promise<T> {
    const startedAt = Date.now();
    try {
      return await call();
    } catch (error) {
      if (!isRevokedCredentialsError(error)) throw error;
      // Credentials fetched after the call started are already new.
      if (this.cached && this.fetchedAt < startedAt) this.cached = null;
      return await call();
    }
  }

  private discard(accessKeyId: string): void {
    if (this.cached?.accessKeyId === accessKeyId) this.cached = null;
  }

  private readonly provider: AwsCredentialIdentityProvider = async () => {
    const session = await currentSession();
    const owner = session.user.username;
    if (this.owner !== owner) {
      this.owner = owner;
      this.cached = null;
      this.pending = null;
    }
    // SDK clients memoize credentials per client INSTANCE, and the portal
    // constructs clients per call, so without this cache every AWS call re-ran
    // GetId + GetCredentialsForIdentity. Those quotas are account-level and
    // shared by every open portal tab ("Rate exceeded", BUG-469). Concurrent
    // calls share one fetch.
    if (this.cached?.expiration && this.cached.expiration.getTime() - Date.now() > EXPIRY_MARGIN_MS) {
      return this.cached;
    }
    if (!this.pending) {
      const pending = this.fetchCredentials()
        .then((creds) => {
          if (this.owner === owner) {
            this.cached = creds;
            this.fetchedAt = Date.now();
          }
          return creds;
        })
        .finally(() => {
          if (this.pending === pending) this.pending = null;
        });
      this.pending = pending;
    }
    return this.pending;
  };

  private async fetchCredentials(): Promise<AwsCredentialIdentity> {
    // Read the session again here: the background token refresh rotates the
    // ID token, and the identity pool refuses an expired one.
    const session = await currentSession();
    const region = getConfigValue('AWS_REGION') || DEFAULT_REGION;
    const identityPoolId = getConfigValue('IDENTITY_POOL_ID');
    const userPoolId = getConfigValue('USER_POOL_ID');
    if (!identityPoolId || !userPoolId) throw new Error(NO_CONFIG);
    return fromCognitoIdentityPool({
      identityPoolId,
      logins: { [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: session.idToken },
      clientConfig: { region, ...CHAIN_RETRY },
    })();
  }
}

export const awsCredentialsService = new AWSCredentialsService();
